import { Link } from 'react-router'

/**
 * The government footer — SGDS's mandatory footer links, on every page.
 *
 * ## Why it exists
 *
 * SGDS's footer component states that **Privacy and Terms of Use links are required on
 * every page**, and calls Contact and Feedback recommended for all services. This console
 * had no footer at all: `git grep '<footer'` over `apps/` returned nothing, and no screen
 * mentioned either obligation.
 *
 * It is the one SGDS foundation a reviewer can check without opening a stylesheet, because
 * it is either on the page or it is not — and this console imports SGDS's own masthead,
 * tokens and components, so the footer was the conspicuous half of the pair to be missing.
 *
 * ## Why the notices are routes and not outbound links
 *
 * The obvious implementation points Privacy and Terms at the Authority's published pages.
 * Candidate URLs on a sibling agency's site were checked while this was written and
 * returned 404 — a link that looks official and goes nowhere is worse than no link, and
 * guessing at an Authority's URL is the same defect as a citation that does not resolve.
 *
 * So each notice is a page in this console, stating what is true of this system, and the
 * Authority's own URLs replace them at deployment. Report Vulnerability is the exception
 * and is outbound: the Vulnerability Disclosure Programme is a whole-of-government intake
 * at tech.gov.sg rather than an agency page, and that URL was verified rather than assumed.
 *
 * ## It renders in two places on purpose
 *
 * `Shell` carries it for every authenticated route, and `LoginPage` carries it as well.
 * The sign-in screen is outside the shell, and it is the page where the obligation matters
 * most — it is where someone decides whether the site asking them to sign in is really
 * government. The notice routes sit outside `RequireAuth` for the same reason: a privacy
 * notice readable only after signing in is not a notice.
 */
export function GovFooter() {
  return (
    <footer className="pt-govfooter pt-no-print">
      <div className="pt-govfooter__inner">
        <nav className="pt-govfooter__links" aria-label="Legal and support">
          {/* Privacy and Terms are the two SGDS requires. They lead. */}
          <Link to="/privacy">Privacy Statement</Link>
          <Link to="/terms">Terms of Use</Link>
          {/* `/contact-us`, not `/contact`: `/contacts` is already a data screen in this
              console (an agency contact directory), and two addresses one letter apart —
              one a legal notice, one a record surface — is a trap for whoever reads a URL
              out over a phone. */}
          <Link to="/contact-us">Contact</Link>
          <Link to="/contact-us#feedback">Feedback</Link>
          <a
            href="https://www.tech.gov.sg/report_vulnerability"
            target="_blank"
            rel="noreferrer noopener"
          >
            Report Vulnerability
            {/* Announced, because a link that changes context without warning fails
                WCAG 3.2.5 for anyone who cannot see the new tab open. */}
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </nav>
        <p className="pt-govfooter__gov">
          A Singapore Government Agency Website. Public Service Division.
        </p>
      </div>
    </footer>
  )
}
